import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import closeIcon from "../../../../public/images/close-icon.svg";
import { FormProvider, useForm } from "react-hook-form";
import { RootState } from "typedefs";
import { useSelector } from "react-redux";
import FormInput from "app/components/Programs/FormInput";
import Label from "app/components/Programs/Label";
import { cls } from "app/utils";
import { schedulingFetchPatch } from "app/utils/request/scheduling/fetch";
import ToolTip from "app/routes/Scheduling/ToolTip";
import { calculateTotalSessions } from "app/components/helpers/CalculateTotalSessions";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import {
  weekdayRestrictionLabel,
  expiry_unit_options,
  daysPerExpiryUnit,
  decomposeExpiry,
  expiryValueRule,
  billingCyclesRule,
} from "../NewOffering/utils";
import SubscriptionStartFields, {
  billStartOptionFor,
  subscriptionStartPayload,
} from "app/routes/Billing/NewOffering/SubscriptionStart";

const EditOfferingModal = ({
  product,
  isModalOpen,
  modalHandler,
  dataRefresher,
}) => {
  const { t } = useTranslation();
  const userId = useSelector((state: RootState) => state.user.self.id);
  const [loading, setLoading] = useState(false);

  const isPackage = product.productType === "product";

  const methods = useForm({
    defaultValues: {
      name: product.title,
      description: product.description || "",
      // CN-2641: type-appropriate config — expiry on packages, cycle cap on
      // subscriptions. Editing affects future grants / new subscriptions only.
      max_billing_cycles: product.maxBillingCycles ?? null,
      ...decomposeExpiry(product.expiresInDays),
      // CN-2832
      bill_start: billStartOptionFor(product.billStartDate),
      billing_start_on: product.billingStartOn ?? null,
      carry_over_sessions: !!product.carryOverSessions,
    },
  });

  const { handleSubmit, watch, setValue, formState } = methods;
  // react-hook-form only tracks dirtyFields once it's read during render; read
  // first inside onSubmit, it's always empty.
  const { dirtyFields } = formState;
  const expires_in_unit = watch("expires_in_unit");

  // CN-2832: only what the coach changed. Re-sending an untouched custom date
  // that has since passed would fail the "must be in the future" check and
  // block an unrelated edit like a new title.
  const changedSubscriptionStart = (data) => {
    const dirty = dirtyFields as Record<string, unknown>;
    const start = subscriptionStartPayload(data);
    return {
      ...(dirty.bill_start || dirty.billing_start_on
        ? { bill_start: start.bill_start, billing_start_on: start.billing_start_on }
        : {}),
      ...(dirty.carry_over_sessions ? { carry_over_sessions: start.carry_over_sessions } : {}),
    };
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const productData = {
      name: data.name,
      description: data.description ? data.description : "",
      id: product.id,
      coach_id: userId,
      // CN-2641: send only the field matching the product type (the API 422s
      // wrong-type fields); an empty value clears back to never/indefinite.
      ...(isPackage
        ? {
            expires_in_days: data.expires_in_value
              ? Number(data.expires_in_value) * daysPerExpiryUnit[data.expires_in_unit.value]
              : null,
          }
        : {
            max_billing_cycles: data.max_billing_cycles
              ? Number(data.max_billing_cycles)
              : null,
            ...changedSubscriptionStart(data),
          }),
    };

    schedulingFetchPatch(`stripe_products/${product.id}`, productData)
      .then(() => {
        modalHandler();
        dataRefresher();
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error updating product:", error);
        setLoading(false);
      });
  };

  const totalSessions = calculateTotalSessions(product);
  const hasRestrictions = product.restrictWeekdays || product.restrictWeekends;

  const restrictionLabels = [];
  if (product.restrictWeekdays) {
    restrictionLabels.push(
      weekdayRestrictionLabel(
        product.weekdayBeginAt ?? 0,
        product.weekdayEndAt ?? 1440,
        t("Weekdays"),
      )
    );
  }
  if (product.restrictWeekends) {
    restrictionLabels.push(t("Weekends & Holidays"));
  }

  return (
    <ModalLayout
      isOpen={isModalOpen}
      onClose={modalHandler}
      classNames={`${styles.modal} event_form_container`}
    >
      <FormProvider {...methods}>
        <form>
          <div className={styles.modal_header}>
            <div className={styles.titleRow}>
              <h2
                className={styles.truncatedText}
                title={product.title?.length > 20 ? product.title : ""}
              >
                {t("Edit")} &quot;
                {product.title?.length > 20
                  ? `${product?.title?.substring(0, 20)}...`
                  : product.title}
                &quot;
              </h2>
              {product.title?.length > 20 && (
                <div className={styles.toolTipAfterEllipsis}>
                  <ToolTip text={product.title} orientation="left" />
                </div>
              )}
            </div>
            <img
              src={closeIcon}
              alt="close"
              className={styles.close_icon}
              onClick={modalHandler}
            />
          </div>

          <div className={styles.modal_body}>
            <div className={styles.contentWrapper}>
              <div className={styles.leftColumn}>
                <div className={styles.titleMargin}>
                  <FormInput
                    tagTitle={t("Name")}
                    fieldName="name"
                    defaultValue={product.name}
                  />
                </div>
                <div className={styles.textarea_width}>
                  <Label
                    text={t("Description")}
                    item={
                      <textarea
                        data-testid="description"
                        {...methods.register("description")}
                        className={styles.details_textarea}
                      />
                    }
                    containerStyle={styles.mt_16}
                  />
                </div>

                {isPackage ? (
                  <div className={cls(styles.titleMargin, styles.expiry_row)}>
                    <div className={styles.expiry_row_item}>
                      <FormInput
                        tagTitle={t("Expires After")}
                        fieldName="expires_in_value"
                        tooltipColor={"gray"}
                        tooltipText={t(
                          "Optional. Credits expire this long after they are granted. Months count as 30 days. Leave empty for credits that never expire. Changes apply to future assignments and purchases only."
                        )}
                        placeholder={t("Never")}
                        // CFTB-3420: the bubble is absolutely positioned, so
                        // whatever it occupies below the icon grows the modal's
                        // scroll height — which is what made the page jump.
                        // Dropping tooltipPlacement="bottom" is the actual fix:
                        // the default opens upward, over the name and
                        // description fields, and adds nothing below. The wider
                        // cap just makes it shorter while it's open (measured:
                        // 213 chars is 168px tall at 200px, 117px at 320px), and
                        // 320 still fits the ~370px column.
                        tooltipMaxWidth={320}
                        validate={expiryValueRule(t)}
                      />
                    </div>
                    <div className={styles.expiry_row_item}>
                      <SelectBox
                        options={expiry_unit_options.map((opt) => ({ value: opt.value, label: t(opt.label) }))}
                        // See the create form: decomposeExpiry hands back the
                        // untranslated option label, and react-select shows the
                        // label from `value` — so an existing package opened in a
                        // non-English locale displayed "Week(s)".
                        value={{ value: expires_in_unit.value, label: t(expires_in_unit.label) }}
                        setValue={(value) => setValue("expires_in_unit", value)}
                        customWidth="100%"
                        customControl={{
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  // Reads "Auto Renewal Ends After [ 3 ] Billing Cycles". The
                  // unit rides inside the field's row, not in a second column
                  // beside it — at half width this label breaks after "Ends".
                  <div className={styles.titleMargin}>
                    <FormInput
                      tagTitle={t("Auto Renewal Ends After")}
                      fieldName="max_billing_cycles"
                      unit={t("Billing Cycles")}
                      tooltipColor={"gray"}
                      tooltipText={t(
                        "Optional. The subscription automatically ends after this many billing cycles. Leave empty to renew until cancelled. Changes apply to new subscriptions only — running subscriptions keep the limit they started with."
                      )}
                      placeholder={t("Unlimited")}
                      // CFTB-3420: see the expiry tooltip above — same cap, same
                      // upward placement, same reason.
                      tooltipMaxWidth={320}
                      validate={billingCyclesRule(t)}
                    />
                  </div>
                )}

                {!isPackage && (
                  <div className={styles.titleMargin}>
                    <SubscriptionStartFields appliesToNewSignupsOnly />
                  </div>
                )}

                <div className={styles.info_wrapper}>
                  <i className={cls("ico ico-info", styles.info_icon)} />
                  <div>
                    {t(
                      "To edit Price or other options you need to Clone and create a new Offering, then Deactivate the old offering",
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.rightColumn}>
                <div className={styles.packageBreakdown}>
                  <strong className={styles.breakdownTitle}>
                    {t("This package includes")}
                  </strong>
                  <ul>
                    {product.rateLinks?.map((rate, idx) => {
                      const isUnlimited = rate.quantity == null;
                      return (
                        <li key={idx} className={styles.sessionRow}>
                          <span className={styles.sessionTitle}>
                            {rate.title ?? "-"}
                          </span>
                          {!isUnlimited && (
                            <span className={styles.count}>
                              {rate.quantity}
                            </span>
                          )}
                          {isUnlimited && (
                            <span className={styles.infinitySymbol}>
                              &infin;
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <hr />
                  <div className={styles.totalRow}>
                    <span className={styles.totalRowSessions}>
                      {t("Total Sessions")}
                    </span>
                    <span>
                      {totalSessions ?? (
                        <span className={styles.infinitySymbol}>&infin;</span>
                      )}
                    </span>
                  </div>
                </div>

                {hasRestrictions && (
                  <div className={styles.usageRestrictions}>
                    <span className={styles.usageRestrictionsLabel}>
                      {t("Usage restricted to:")}
                    </span>
                    <span className={styles.usageRestrictionsValue}>
                      {restrictionLabels.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.button_wrapper}>
              <Button
                buttonType="secondary-outline"
                title={t("Cancel")}
                onClickFunc={modalHandler}
              />
              <Button
                buttonType="primary"
                title={loading ? t("Saving...") : t("Save")}
                onClickFunc={handleSubmit(onSubmit)}
                disabled={loading}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </ModalLayout>
  );
};

export default memo(EditOfferingModal);
